import { Card, Gate, Head, ScrollX, State, useAsync } from "@authoxi/ui-shared";
import { client, leaderboard } from "../api";

/**
 * Public rankings.
 *
 * The design called for `agents` and `% signed` columns. Neither can exist: an Agent row has no
 * host, this directory deliberately does not list agents, and authoxi never observes a third
 * party's traffic. Both would have been invented numbers on the one page whose entire value is
 * that its numbers are not invented — so the columns are `checks passed` and `checked`, which
 * are what the grade is actually made of.
 */
export function Leaderboard() {
  const { data, loading, error } = useAsync(() => leaderboard(25));

  return (
    <>
      <Head
        pill="Public rankings"
        title="Who is doing this properly."
        lede="Built from public scans of public endpoints. Ranked by the most recent grade, never the best one — a leaderboard you can climb by scanning repeatedly is a leaderboard nobody believes."
      />

      {loading && <State kind="loading" title="Reading the corpus…" />}
      {error && <State kind="error" title="Could not load the leaderboard" detail={error} />}

      {data && data.ranking.length === 0 && (
        <State
          kind="empty"
          title="Nothing has been scanned yet."
          detail="The ranking is built from endpoints somebody chose to run through the grader. Be the first."
          action={
            <a className="btn btn-go" href="/tools/grader" style={{ marginTop: "var(--s4)" }}>
              Grade an endpoint
            </a>
          }
        />
      )}

      {data && data.ranking.length > 0 && (
        <>
          <Card pad>
            <ScrollX>
              <table className="table" data-testid="leaderboard">
                <thead>
                  <tr>
                    <th />
                    <th>Host</th>
                    <th>Grade</th>
                    <th>Score</th>
                    <th>Checks</th>
                    <th>Checked</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.ranking.map((r) => (
                    <tr key={r.host}>
                      <td className="rank">{String(r.rank).padStart(2, "0")}</td>
                      <td className="host">{r.host}</td>
                      <td>
                        <span className={`mini-grade g-${r.letter}`}>{r.letter}</span>
                      </td>
                      <td className="mono">{r.score}</td>
                      <td className="mono muted">
                        {r.checks_passed}/{r.checks_total}
                      </td>
                      <td className="sm muted">{r.checked}</td>
                      <td style={{ textAlign: "right" }}>
                        <a className="btn" href={`/tools/directory/${r.host}`}>
                          View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          </Card>

          {/* What the sample IS, next to the number rather than in a footnote. The integrity of
              this page rests entirely on not letting it read as a census of the internet. */}
          <p className="sm muted" style={{ marginTop: "var(--s4)" }}>
            {data.sample_size} endpoints. {data.sample_note} Measures {data.measures}.
          </p>
          <p className="sm muted" style={{ marginTop: "var(--s2)" }}>
            Hosts that never answered are graded but not listed — publishing “we scanned a host
            that does not exist and it scored F” is a claim about something we never reached.
          </p>

          <Gate
            client={client}
            source="leaderboard"
            title="The monthly cut"
            body="Who moved, who appeared, and what the median grade did. One email a month, no other use of your address."
            cta="Send me the monthly"
          />
        </>
      )}
    </>
  );
}
